import { computed, onScopeDispose, ref } from 'vue'
import { io, type Socket } from 'socket.io-client'
import {
  WS_EVENT_MESSAGE,
  WS_EVENT_SUBSCRIBE,
  WS_EVENT_UNSUBSCRIBE,
  type WsMessage,
  type WsSubscribeAck
} from '§A/websocket/messages.type'
import { useKeycloak } from '@/shared/composables/useKeycloak'

type WsStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error'
type WsHandler<T = unknown> = (payload: T, message: WsMessage<string, T>) => void

// Singleton socket shared across every useWS() call — opening multiple
// Socket.IO connections per page would multiply auth overhead and topic
// bookkeeping with no upside.
let socket: Socket | null = null
const status = ref<WsStatus>('idle')
const socketId = ref<string | null>(null)
const subscribers = new Map<string, Set<WsHandler>>()

// Build the Socket.IO base URL from the API env vars. Socket.IO mounts on
// the same HTTP server as the REST API, so we reuse the API scheme/host/port
// rather than introducing parallel VITE_WS_* env vars.
const buildBaseUrl = (): string => {
  const apiScheme = import.meta.env.VITE_APISCHEME || window.location.protocol.replace(':', '')
  const scheme = apiScheme === 'https' ? 'https' : 'http'
  const host = import.meta.env.VITE_APIHOST || window.location.hostname
  const port = import.meta.env.VITE_APIPORT
  return `${scheme}://${host}${port ? `:${port}` : ''}`
}

const ensureSocket = (): Socket => {
  if (socket) return socket

  const { getToken, refreshToken } = useKeycloak()

  status.value = 'connecting'
  socket = io(buildBaseUrl(), {
    // Mounted at /ws/ to match the existing nginx upgrade rule — see
    // ws.gateway.ts on the backend, which must use the same path.
    path: '/ws/',
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
    autoConnect: true,
    // The auth callback runs on every connection attempt, so reconnects pick
    // up a refreshed access token automatically.
    auth: (cb) => cb({ token: getToken() ?? '' })
  })

  socket.on('connect', () => {
    status.value = 'connected'
    socketId.value = socket!.id ?? null
    // Re-subscribe every active topic after a reconnect. Local handlers
    // remained in `subscribers`, but the server forgot we joined those rooms.
    for (const topic of subscribers.keys()) {
      socket!.emit(WS_EVENT_SUBSCRIBE, { topic })
    }
  })

  socket.on('disconnect', () => {
    status.value = 'disconnected'
    socketId.value = null
  })

  socket.on('connect_error', async (err) => {
    status.value = 'error'
    // Server rejected the handshake — most likely the access token expired
    // between the page load and the connection attempt. Force a refresh; the
    // built-in reconnection logic will pick up the new token via the auth
    // callback on the next attempt.
    if (err?.message === 'UNAUTHORIZED') {
      try {
        await refreshToken()
      }
      catch {
        // refreshToken handles terminal failure (logs the user out).
      }
    }
  })

  socket.on(WS_EVENT_MESSAGE, (msg: WsMessage<string, unknown>) => {
    if (!msg || typeof msg.topic !== 'string') return
    const handlers = subscribers.get(msg.topic)
    if (!handlers) return
    for (const handler of handlers) {
      try {
        handler(msg.payload, msg)
      }
      catch (err) {
        console.error(`[useWS] handler for topic=${msg.topic} threw`, err)
      }
    }
  })

  return socket
}

export const useWS = () => {
  // Eagerly opens the socket without subscribing. Useful for test/diagnostic
  // pages; normal callers can rely on subscribe() to open lazily.
  const connect = (): void => {
    ensureSocket()
  }

  // Closes the socket and clears the singleton. Local subscriber handlers
  // are preserved so the next connect()/subscribe() can rejoin transparently
  // — mirrors the reconnect-rejoin behavior wired into the 'connect' handler.
  const disconnect = (): void => {
    if (!socket) return
    socket.disconnect()
    socket = null
    status.value = 'disconnected'
    socketId.value = null
  }

  // Emit a client → server event, optionally with an ack callback. Defers until
  // the socket is connected (mirrors subscribe()'s join-on-connect behavior) so
  // callers don't have to gate on connection status themselves.
  const emit = <TAck = unknown>(
    event: string,
    payload?: unknown,
    ack?: (response: TAck) => void
  ): void => {
    const s = ensureSocket()
    const doEmit = (): void => {
      if (ack) s.emit(event, payload, ack)
      else s.emit(event, payload)
    }
    if (s.connected) doEmit()
    else s.once('connect', doEmit)
  }

  const subscribe = <T = unknown>(topic: string, handler: WsHandler<T>): (() => void) => {
    const s = ensureSocket()
    let handlers = subscribers.get(topic)
    const firstHandlerForTopic = !handlers
    if (!handlers) {
      handlers = new Set<WsHandler>()
      subscribers.set(topic, handlers)
    }
    handlers.add(handler as WsHandler)

    const joinOnServer = (): void => {
      s.emit(WS_EVENT_SUBSCRIBE, { topic }, (ack: WsSubscribeAck | undefined) => {
        if (ack && !ack.ok) {
          console.warn(`[useWS] subscribe rejected for topic=${topic}: ${ack.error}`)
        }
      })
    }

    if (firstHandlerForTopic) {
      if (s.connected) joinOnServer()
      else s.once('connect', joinOnServer)
    }

    const unsubscribe = (): void => {
      const set = subscribers.get(topic)
      if (!set) return
      set.delete(handler as WsHandler)
      if (set.size === 0) {
        subscribers.delete(topic)
        if (s.connected) s.emit(WS_EVENT_UNSUBSCRIBE, { topic })
      }
    }

    // Auto-clean when the caller's effect scope tears down (component
    // unmount). Pass failSilently=true so calling from a non-Vue scope (e.g.
    // a Pinia store action) doesn't emit warnings.
    onScopeDispose(unsubscribe, true)
    return unsubscribe
  }

  return {
    status: computed(() => status.value),
    socketId: computed(() => socketId.value),
    connect,
    disconnect,
    subscribe,
    emit
  }
}
